<template>
  <div>
    <form
      method="post"
      v-if="(briefing && !briefingSent) || !briefing"
      @submit.prevent="validate"
      class="job-post-form"
    >
      <JobDetailHeader
        v-if="currentView === 'review'"
        :isPreview="currentView === 'review'"
        :job="fields"
        @set-current-view="$emit('set-current-view', 'edit')"
      />
      <div class="responsive-width mx-auto">
        <job-view
          v-if="currentView === 'review'"
          :job="fields"
          :isPreview="currentView === 'review'"
        />
        <div v-if="currentView === 'edit'">
          <div class="form-group">
            <text-input
              label="Job title *"
              :hasError="!!errors.name"
              :errMsg="errors.name"
              v-model="fields.name"
            >
            </text-input>
          </div>

          <div class="form-group font-16-500">
            <label>Job Description and Qualifications</label>
            <QuillEditor
              v-model="fields.description"
              placeholder="Brief description of the position"
            />
          </div>

          <div class="form-group font-16-500">
            <label>Job Trade Categories</label>
            <TradeTypeahead
              v-model="fields.tradeCategories"
              :allTrades="tradeCategoryOptions"
              :defaultTrades="[]"
            />
          </div>

          <div class="form-group">
            <text-input
              label="Zip Code*"
              v-model="fields.zip"
              :hasError="!!errors.zip"
              :errMsg="errors.zip"
            >
            </text-input>
          </div>

          <gfb-radio-group
            label="Type of Position"
            name="type"
            :options="positionOptions"
            :value="fields.type"
            :required="true"
            :error="errors.type"
            @change="fields.type = $event"
          />

          <!-- <b-form-group label="Qualifications" class="font-16-500">
            <div @keydown.enter.prevent.stop>
              <CustomMultiSelect
                :selectOptions="fields.qualifications"
                :selectedList="fields.qualifications"
                selectLabel="Describe the qualifications"
                :showNoOptionsFlag="false"
                :tagFlag="true"
                @handle-tag="handleQualificationsTag"
                @update-selected="updateQualifications"
              ></CustomMultiSelect>
            </div>
          </b-form-group> -->

          <div class="form-group">
            <text-input label="Compensation" v-model="fields.compensation">
            </text-input>
          </div>

          <div class="form-group font-16-500">
            <label>Select any benefits that apply (PTO, Bonus, etc.)</label>
            <div @keydown.enter.prevent.stop>
              <CustomMultiSelect
                :selectOptions="benefitsOptionNames"
                :selectedList="fields.benefits"
                selectLabel="Add benefits"
                :showNoOptionsFlag="false"
                @update-selected="updateBenefits"
              ></CustomMultiSelect>
            </div>
          </div>

          <div class="form-group font-16-500">
            <label>Why Work Here</label>
            <QuillEditor
              v-model="fields.whyWorkHere"
              placeholder="Tell potential candidates why they would like to work here."
            />
          </div>

          <gfb-radio-group
            label="Does this job require experience?"
            name="experienceRequired"
            :options="trueFalse"
            :value="fields.experienceRequired"
            :required="true"
            :error="errors.experienceRequired"
            @change="fields.experienceRequired = $event"
          />
          <gfb-radio-group
            label="Are you willing to provide paid training for this position?"
            name="willingToProvidePaidTraining"
            :options="trueFalse"
            :value="fields.willingToProvidePaidTraining"
            :required="true"
            :error="errors.willingToProvidePaidTraining"
            @change="fields.willingToProvidePaidTraining = $event"
          />
        </div>
      </div>

      <FieldsErrorMessage
        :show="currentView === 'edit' && formMessage.missingFields"
        type="missing_fields"
      />

      <FieldsErrorMessage
        :show="currentView === 'edit' && formMessage.reviewIssues"
        type="review_fields"
      />

      <div
        class="job-post-actions d-flex justify-space-between align-items-end pb-4 pt-3 mx-auto"
      >
        <DefaultButton
          v-if="currentView === 'review'"
          :labelText="`Back`"
          @click="$emit('set-current-view', 'edit')"
          :disabled="loading"
        />
        <DefaultButton
          v-if="currentView === 'edit' && !isSendingJobPost"
          :labelText="`Cancel`"
          @click="handleCancel"
        />

        <span class="ml-auto d-flex flex-column align-items-end missing-field">
          <!--span
            v-if="currentView === 'review' && !briefing"
            class="font-14-400 mr-4"
            >This posting will be active for 30 days</span
          -->
          <span
            v-if="currentView === 'review' && briefing"
            class="font-14-400 mr-4"
            >Be sure to confirm all fields before sending to the candidate</span
          >
          <DefaultButton
            :labelText="mainButtonLabel"
            @click="validate"
            type="submit"
            :disabled="disablePublish"
          />
        </span>
      </div>
    </form>
    <div
      v-else-if="briefing && briefingSent && skilledWorker"
      class="w-50 py-4 mx-auto"
    >
      <h2>Success! Your job briefing was sent to {{ skilledWorker.name }}</h2>
      <p>
        If your skilled worker match accepts your job briefing, you will have
        full access to their contact information to arrange an interview and
        finalize the hiring process. If the job briefing is declined, we'll
        request feedback from them to give back to you.
      </p>
      <base-button
        class="rounded-lg"
        type="success"
        @click="goHome"
        :loading="loading"
      >
        Back to Dashboard
      </base-button>
      <DefaultButton :labelText="'Back to Dashboard'" @click="goHome" />
    </div>
    <div
      v-else-if="briefing && briefingSent && jobSeeker"
      class="w-50 py-4 mx-auto"
    >
      <h2>Success! Your job briefing was sent to {{ jobSeeker.name }}</h2>
      <p>
        If the job seeker accepts your job briefing, you will have full access
        to their contact information to arrange an interview and finalize the
        hiring process. If the job briefing is declined, we'll request feedback
        from them to give back to you.
      </p>
      <DefaultButton :labelText="'Back to Dashboard'" @click="goHome" />
    </div>
  </div>
</template>

<script>
import { nanoid } from "nanoid"
import jobs from "@/api/jobs.js"
import messagesMixin from "@/mixins/messagesMixin"
import options from "@/utils/options"
import { normalize } from "@/utils/validations"
import JobView from "./JobView"
import profile from "@/api/profile"
import DefaultButton from "@/components/DefaultButton"
import CustomMultiSelect from "@/components/CustomMultiSelect"
import { ERROR_MESSAGES, API_RESPONSE_ERRORS } from "@/utils/constants"
import JobDetailHeader from "@/components/Job/JobDetailHeader"
import QuillEditor from "@/components/QuillEditor.vue"
import FieldsErrorMessage from "@/components/FieldsErrorMessage"
import TradeTypeahead from "@/components/TradeTypeahead"

export default {
  mixins: [messagesMixin],
  components: {
    JobView,
    DefaultButton,
    CustomMultiSelect,
    JobDetailHeader,
    QuillEditor,
    FieldsErrorMessage,
    TradeTypeahead,
  },
  props: {
    job: Object,
    loading: {
      type: Boolean,
      required: true,
    },
    currentView: {
      type: String,
      required: true,
    },
    cancelButtonTo: {
      type: String,
      required: true,
    },
    briefing: {
      type: Boolean,
      required: false,
    },
    skilledWorker: {
      type: Object,
      required: false,
    },
    jobSeeker: {
      type: Object,
      required: false,
    },
    isCopyMode: {
      type: Boolean,
      default: false,
    },
    labelText: {
      type: String,
      default: "Publish",
    },
    isSendingJobPost: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const isPriorityAccess = this.$route.path.includes("skilled-workers")
    const isSkilledWorker =
      this.jobSeeker?.jobSeeker?.hasHandsOnExperience ||
      this.jobSeeker?.jobSeeker?.hasFormalTraining
    const job =
      this.job ??
      (localStorage.getItem("jobFields")
        ? JSON.parse(localStorage.getItem("jobFields"))
        : {})
    const {
      name = "",
      description = "",
      zip = "",
      type = "",
      qualifications = [],
      compensation = "",
      benefits = [],
      whyWorkHere = "",
      willingToProvidePaidTraining,
      experienceRequired,
    } = job
    const tradeCategories =
      job.tradeCategories ||
      this.$store.state.userProfile.selfPerformWorkCategories
    return {
      company: this.$store.state.userProfile?._id,
      user: this.$store.state.user.userId,
      jobId: null,
      fields: {
        name,
        description,
        zip,
        type,
        qualifications,
        tradeCategories,
        compensation,
        benefits,
        whyWorkHere,
        willingToProvidePaidTraining,
        experienceRequired,
      },
      trueFalse: options.trueFalse,
      positionOptions: options.positionOptions,
      jobBenefitsOptions: options.jobBenefitsOptions,
      briefingSent: false,
      dataRetrieving: false,
      checkout: false,
      returnUrlPath: `/dashboard`,
      loadingChild: this.loading,
      paymentIntent: null,
      isPriorityAccess,
      isSkilledWorker,
      benefitsOptionNames: [],
      disablePublish: false,
      hasSubmitted: false,
      globalErrors: {
        missingFields: false,
        reviewIssues: false,
      },
    }
  },
  computed: {
    errors() {
      return this.$store.getters["jobForm/errors"] || {}
    },
    formMessage() {
      return (
        this.$store.getters["jobForm/formMessage"] || {
          show: false,
          type: null,
        }
      )
    },
    hasValidationErrors() {
      return Object.keys(this.errors || {}).length > 0
    },

    tradeCategoryOptions() {
      return this.$store.state.categories.map((c) => c.name)
    },
    mainButtonLabel() {
      if (this.labelText === "Send" && this.isSendingJobPost) return "Send"
      if (this.briefing && this.currentView === "review") {
        return "Send Job Briefing"
      }
      return "Publish"
    },
    hasRequiredFieldsEmpty() {
      const {
        name,
        zip,
        type,
        willingToProvidePaidTraining,
        experienceRequired,
      } = this.fields
      return (
        !name ||
        !zip ||
        !type ||
        willingToProvidePaidTraining === null ||
        willingToProvidePaidTraining === undefined ||
        experienceRequired === null ||
        experienceRequired === undefined
      )
    },
  },
  methods: {
    removeTrade(option) {
      this.fields.tradeCategories = this.fields.tradeCategories.filter(
        (category) => category !== option
      )
    },
    addTrade(option) {
      if (!this.fields.tradeCategories.includes(option)) {
        this.fields.tradeCategories.push(option)
      }
    },
    selectQualification(option) {
      let label = option.label ? option.label : option
      if (!this.fields.qualifications.includes(label)) {
        this.fields.qualifications.push(label)
      }
    },
    removeQualification(option) {
      this.fields.qualifications = this.fields.qualifications.filter(
        (qualification) => qualification !== option
      )
    },
    updateQualifications(selected) {
      this.fields.qualifications = selected
    },
    handleQualificationsTag(newTag) {
      if (!this.fields.qualifications.includes(newTag)) {
        this.fields.qualifications = this.fields.qualifications.concat(newTag)
      }
    },
    selectBenefit(option) {
      let label = option.label ? option.label : option
      if (!this.fields.benefits.includes(label)) {
        this.fields.benefits.push(label)
      }
    },
    removeBenefit(option) {
      this.fields.benefits = this.fields.benefits.filter(
        (benefit) => benefit !== option
      )
    },
    updateBenefits(selected) {
      this.fields.benefits = selected
    },
    handleBenefitsTag(newTag) {
      if (!this.fields.benefits.includes(newTag)) {
        this.fields.benefits = this.fields.benefits.concat(newTag)
      }
    },
    handleTradeCategoriesTag(newTag) {
      this.fields.tradeCategories = this.fields.tradeCategories.concat(newTag)
    },
    async validate() {
      this.$store.commit("jobForm/SET_FIELDS", this.fields)

      const isValid = await this.$store.dispatch("jobForm/validateJobForm")

      if (!isValid) return

      this.handleSubmit({
        ...this.fields,
        published: true,
      })
    },
    async createPaymentIntent() {
      const data = {
        candidate_profile_id: this.profileId,
        product_slug: this.productSlug,
        return_url: `${process.env.VUE_APP_SITE_URL}/employees/skilled-workers/profile/${this.profileId}/access/checkout-complete`,
        idempotencyKey: nanoid(),
      }
      const defaultError = new Error("We couldn't submit your payment request.")
      try {
        const response = await this.$store.dispatch(
          "api/createPaymentIntent",
          data
        )
        if (!response.data || response.data.errors) {
          throw response.data?.errors[0] ?? defaultError
        }
        const paymentIntent = response.data
        console.log("paymentIntent", paymentIntent)
        this.loading = false
        this.$router.push({ query: { payment_intent: paymentIntent.id } })
      } catch (e) {
        console.error(e)
      }
    },
    goHome() {
      this.$router.push("/dashboard")
    },
    async createJob() {
      const { data } = await jobs.postJob(normalize(this.fields), null)
      this.jobId = data.id
      console.log("DATA", data)
      this.$router.push(`${this.returnUrlPath}`)
    },
    async retrievePaymentIntent() {
      this.$emit("set-data-retrieving", true)
      const id = this.$route.query?.payment_intent
      if (id) {
        try {
          const { data } = await this.$store.dispatch("api/getPaymentIntent", {
            id,
          })
          if (!data) {
            throw new Error(this.defaultErrorMessage)
          }
          if (data.errors) {
            throw data.errors[0] ?? new Error(this.defaultErrorMessage)
          }
          this.paymentIntent = data
          this.$emit("set-data-retrieving", false)
        } catch (error) {
          this.logDevError(error)
          this.$emit("set-data-retrieving", false)
        }
      }
    },
    handleCancel() {
      localStorage.removeItem("copiedJobId")
      this.$router.push(this.cancelButtonTo)
    },
    async handleSubmit(fields) {
      this.$emit("set-loading", true)
      this.disablePublish = true
      const isEditing = !!this.job && !this.isCopyMode
      const createOrUpdate = isEditing ? jobs.updateJob : jobs.postJob
      try {
        if (!isEditing && this.briefing) {
          // CREATE JOB BRIEFING
          fields.briefing = true
          fields.jobSeeker = this.$route.params.profileId
          let { data } = await createOrUpdate(fields, null)
          if (data.id) {
            const payload = {
              company: this.$store.state.userProfile.name,
              jobBriefing: data.id,
              userId: this.$route.params.profileId,
            }
            let recipientProfileId = this.$route.params?.profileId
            let id = data.id
            let type = "job"
            await profile.requestProfileAccess(recipientProfileId, id, type)
            // SEND EMAIL BRIEFING
            await this.$store.dispatch("api/sendJobBriefing", payload)
            // REDIRECT TO SUCCESS PAGE
            this.briefingSent = true
            this.$emit("set-current-view", "done")
            await this.$store.dispatch("api/getUserProfile", {
              userId: this.$store.state.user._id,
            })
          }
          return
        }
        const { data } = await createOrUpdate(
          isEditing ? this.job._id : fields,
          isEditing ? fields : null
        )
        console.log("DATA", data)
        if (data.id && !this.isSendingJobPost) {
          localStorage.removeItem("jobFields")
          this.$toast.success("Congratulations, your job has been published!")
          this.$router.push(`/employees/jobs/${data.id}`)
        }
        if (this.isSendingJobPost) {
          this.$emit("job-saved", data)
          this.$emit("close-modal")
        }
      } catch (error) {
        if (
          error.response?.status == 400 &&
          error.response?.data?.error ===
            API_RESPONSE_ERRORS.INVALID_ZIP_CODE_ERROR
        ) {
          this.showErrorMessage(
            error?.errors?.[0],
            ERROR_MESSAGES.INVALID_ZIP_CODE_INPUT
          )
        } else {
          this.$toast.error(
            error.message ||
              `We couldn't ${isEditing ? "update" : "create"} this job.`
          )
        }
      } finally {
        this.disablePublish = false
        this.$emit("set-loading", false)
        localStorage.removeItem("copiedJobId")
      }
    },
    async handleConfirm() {
      this.$refs.productPurchase.handleConfirm()
    },
    setLoading(loading) {
      this.loadingChild = loading
    },
    setDataRetrieving(isRetrieving) {
      this.dataRetrieving = isRetrieving
    },
    goToCheckout() {
      this.checkout = !this.checkout
    },
    async getBenefitsOptions() {
      await this.$store
        .dispatch("api/getCompanyBenefitsOptions")
        .then((response) => {
          if (response.data) {
            this.benefitsOptionNames = response.data.map(
              (option) => option.name
            )
          } else {
            this.$toast.error("Error retrieving benefits options.")
          }
        })
    },
  },
  async created() {
    this.dataRetrieving = false
    this.getBenefitsOptions()
  },
}
</script>

<style lang="scss" scoped>
.responsive-width {
  width: 80%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
}

.form-group {
  margin-top: 1.5rem;
}

sup.required {
  color: var(--Red-500, #e24646);
}

.missing-required-fields {
  color: var(--Red-500, #e24646);
}

.job-post-actions {
  width: 80%;

  @media only screen and (max-width: 600px) {
    width: 100%;
  }
}
</style>
